import { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, UserPlus, TrashSimple, MapPin, Path, EnvelopeSimple, Users, ChartBar, ListChecks, CalendarCheck, PencilSimple, Money, Clock } from "@phosphor-icons/react";

const NAV = [
  { key: "overview", to: "/admin", label: "Overview" },
  { key: "tasks", to: "/admin/tasks", label: "Tasks" },
  { key: "attendance", to: "/admin/attendance", label: "Attendance" },
  { key: "staff", to: "/admin/staff", label: "Staff" },
  { key: "payroll", to: "/admin/payroll", label: "Payroll" },
  { key: "reports", to: "/admin/reports", label: "Reports" },
];

const STATUS_COLORS = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  deferred: "bg-rose-50 text-rose-700 border-rose-200",
};

function StatCard({ label, value, hint, icon: Icon, tone = "indigo", testid }) {
  const toneMap = {
    indigo: "text-indigo-600 bg-indigo-50",
    emerald: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    rose: "text-rose-600 bg-rose-50",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="stat-card rounded-2xl p-5 relative" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          {Icon ? <Icon size={16} weight="duotone" /> : null}
        </div>
      </div>
      <div className="mt-3 font-display font-black tracking-tighter text-4xl">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function CreateTaskDialog({ staff, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", assigned_to: "", due_date: new Date().toISOString().slice(0, 10), priority: "medium" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tasks", form);
      toast.success("Task assigned");
      setOpen(false);
      setForm({ ...form, title: "", description: "" });
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-task-button" className="rounded-full bg-indigo-600 hover:bg-indigo-700">
          <Plus size={16} className="mr-1" /> New task
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="new-task-dialog">
        <DialogHeader><DialogTitle className="font-display">Assign a task</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" className="mt-2" />
          </div>
          <div>
            <Label className="overline">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="task-desc-input" className="mt-2" rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="overline">Assign to</Label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger className="mt-2" data-testid="task-assignee-trigger"><SelectValue placeholder="Select staff" /></SelectTrigger>
                <SelectContent>
                  {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Due date</Label>
              <Input type="date" required value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="task-due-input" className="mt-2" />
            </div>
          </div>
          <div>
            <Label className="overline">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger className="mt-2" data-testid="task-priority-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !form.assigned_to} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="task-submit-button">
              {saving ? "Saving…" : "Assign task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreateStaffDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "staff", hourly_rate: 0, km_rate: 2 });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.role} created`);
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "staff", hourly_rate: 0, km_rate: 2 });
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-staff-button" className="rounded-full bg-slate-900 hover:bg-slate-800">
          <UserPlus size={16} className="mr-1" /> Add user
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="new-staff-dialog">
        <DialogHeader><DialogTitle className="font-display">Add team member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="overline">Name</Label><Input required className="mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" /></div>
          <div><Label className="overline">Email</Label><Input required type="email" className="mt-2" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email-input" /></div>
          <div><Label className="overline">Phone</Label><Input className="mt-2" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone-input" /></div>
          <div><Label className="overline">Temp password</Label><Input required type="text" className="mt-2 font-mono" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password-input" /></div>
          <div>
            <Label className="overline">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-2" data-testid="staff-role-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role === "staff" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Hourly rate (₹/hr)</Label>
                <Input type="number" step="0.5" min="0" className="mt-2 font-mono" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} data-testid="staff-hourly-input" />
              </div>
              <div>
                <Label className="overline">KM rate (₹/km)</Label>
                <Input type="number" step="0.5" min="0" className="mt-2 font-mono" value={form.km_rate} onChange={(e) => setForm({ ...form, km_rate: parseFloat(e.target.value) || 0 })} data-testid="staff-km-input" />
              </div>
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={saving} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="staff-submit-button">{saving ? "Saving…" : "Create"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRatesDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [hourly, setHourly] = useState(user.hourly_rate ?? 0);
  const [km, setKm] = useState(user.km_rate ?? 2);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}/rates`, { hourly_rate: hourly, km_rate: km });
      toast.success("Rates updated");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" data-testid={`edit-rates-${user.id}`} className="text-slate-500 hover:text-indigo-600"><PencilSimple size={14} /></Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Rates · {user.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="overline">Hourly rate (₹/hr)</Label>
            <Input type="number" step="0.5" min="0" className="mt-2 font-mono" value={hourly} onChange={(e) => setHourly(parseFloat(e.target.value) || 0)} data-testid={`edit-hourly-${user.id}`} />
          </div>
          <div>
            <Label className="overline">KM rate (₹/km)</Label>
            <Input type="number" step="0.5" min="0" className="mt-2 font-mono" value={km} onChange={(e) => setKm(parseFloat(e.target.value) || 0)} data-testid={`edit-km-${user.id}`} />
          </div>
          <DialogFooter><Button type="submit" disabled={saving} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid={`edit-rates-save-${user.id}`}>{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TaskRow({ task, onDelete }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`task-row-${task.id}`}>
      <div className="col-span-5">
        <div className="font-semibold text-slate-900">{task.title}</div>
        {task.description && <div className="text-xs text-slate-500 line-clamp-1">{task.description}</div>}
      </div>
      <div className="col-span-2 text-sm text-slate-700">{task.assigned_to_name}</div>
      <div className="col-span-2 text-sm text-slate-700 font-mono">{task.due_date}</div>
      <div className="col-span-1 text-sm text-slate-900 font-semibold">{(task.distance_km || 0).toFixed(2)}</div>
      <div className="col-span-1"><Badge variant="outline" className={`${STATUS_COLORS[task.status]} border`}>{task.status.replace("_", " ")}</Badge></div>
      <div className="col-span-1 text-right">
        <Button variant="ghost" size="icon" onClick={() => onDelete(task.id)} data-testid={`delete-task-${task.id}`} className="text-slate-400 hover:text-rose-600"><TrashSimple size={16} /></Button>
      </div>
    </div>
  );
}

function Overview() {
  const [report, setReport] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [d, m] = await Promise.all([api.get("/reports/daily"), api.get("/reports/monthly-km")]);
      setReport(d.data); setMonthly(m.data);
    } catch (e) { toast.error("Failed to load report"); }
  };
  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send");
      toast.success(`Reminders sent to ${data.sent} staff (${data.total_open} open tasks)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const totals = report?.summary?.reduce(
    (a, s) => ({
      total: a.total + s.total,
      completed: a.completed + s.completed,
      pending: a.pending + s.pending + s.in_progress,
      km: a.km + s.distance_km,
    }),
    { total: 0, completed: 0, pending: 0, km: 0 }
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">{report?.date || "Today"}</div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Daily Operations</h1>
          <p className="text-slate-500 mt-1">Live snapshot of tasks and field distance.</p>
        </div>
        <Button onClick={sendReminders} disabled={sending} variant="outline" className="rounded-full" data-testid="send-reminders-button">
          <EnvelopeSimple size={16} className="mr-2" /> {sending ? "Sending…" : "Send email reminders"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-total" label="Tasks today" value={totals?.total ?? 0} icon={ListChecks} tone="slate" />
        <StatCard testid="stat-completed" label="Completed" value={totals?.completed ?? 0} icon={CalendarCheck} tone="emerald" />
        <StatCard testid="stat-pending" label="Open" value={totals?.pending ?? 0} icon={MapPin} tone="amber" />
        <StatCard testid="stat-km" label="KM today" value={(totals?.km ?? 0).toFixed(2)} icon={Path} tone="indigo" />
      </div>

      <div>
        <div className="overline mb-3">Per-staff · today</div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-3">Staff</div>
            <div className="col-span-2">Total</div>
            <div className="col-span-2">Completed</div>
            <div className="col-span-2">Open</div>
            <div className="col-span-3">KM</div>
          </div>
          {(report?.summary || []).map((s) => (
            <div key={s.user.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`staff-daily-${s.user.id}`}>
              <div className="col-span-3 font-semibold">{s.user.name}</div>
              <div className="col-span-2 font-mono">{s.total}</div>
              <div className="col-span-2 font-mono text-emerald-600">{s.completed}</div>
              <div className="col-span-2 font-mono text-amber-600">{s.pending + s.in_progress + s.deferred}</div>
              <div className="col-span-3 font-mono">{s.distance_km.toFixed(2)} km</div>
            </div>
          ))}
          {(report?.summary || []).length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No staff yet</div>}
        </div>
      </div>

      <div>
        <div className="overline mb-3">Monthly · {monthly?.month}</div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-6">Staff</div>
            <div className="col-span-3">Completed tasks</div>
            <div className="col-span-3">Total KM</div>
          </div>
          {(monthly?.rows || []).map((r) => (
            <div key={r.user.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`staff-monthly-${r.user.id}`}>
              <div className="col-span-6 font-semibold">{r.user.name}</div>
              <div className="col-span-3 font-mono">{r.tasks}</div>
              <div className="col-span-3 font-mono font-bold text-indigo-600">{r.distance_km.toFixed(2)} km</div>
            </div>
          ))}
          {(monthly?.rows || []).length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No staff yet</div>}
        </div>
      </div>
    </div>
  );
}

function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filterStaff, setFilterStaff] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    const params = {};
    if (filterStaff !== "all") params.assigned_to = filterStaff;
    if (filterStatus !== "all") params.status_f = filterStatus;
    const [t, u] = await Promise.all([
      api.get("/tasks", { params }),
      api.get("/users"),
    ]);
    setTasks(t.data);
    setStaff(u.data.filter((x) => x.role === "staff"));
  };
  useEffect(() => { load(); }, [filterStaff, filterStatus]);

  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">All tasks</div>
          <h1 className="font-display font-black tracking-tighter text-4xl">Task board</h1>
        </div>
        <CreateTaskDialog staff={staff} onCreated={load} />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="overline">Staff</span>
          <Select value={filterStaff} onValueChange={setFilterStaff}>
            <SelectTrigger className="w-44" data-testid="filter-staff-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="overline">Status</span>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-44" data-testid="filter-status-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="deferred">Deferred</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="tasks-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
          <div className="col-span-5">Title</div>
          <div className="col-span-2">Staff</div>
          <div className="col-span-2">Due</div>
          <div className="col-span-1">KM</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1"></div>
        </div>
        {tasks.map((t) => (<TaskRow key={t.id} task={t} onDelete={remove} />))}
        {tasks.length === 0 && <div className="p-10 text-center text-slate-400 text-sm">No tasks match these filters.</div>}
      </div>
    </div>
  );
}

function StaffTab() {
  const [users, setUsers] = useState([]);
  const load = async () => { const { data } = await api.get("/users"); setUsers(data); };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Team</div>
          <h1 className="font-display font-black tracking-tighter text-4xl">Staff & Admins</h1>
        </div>
        <CreateStaffDialog onCreated={load} />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
          <div className="col-span-3">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1">Role</div>
          <div className="col-span-2">Hourly ₹/hr</div>
          <div className="col-span-2">KM ₹/km</div>
          <div className="col-span-1"></div>
        </div>
        {users.map((u) => (
          <div key={u.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100 items-center" data-testid={`user-row-${u.id}`}>
            <div className="col-span-3">
              <div className="font-semibold">{u.name}</div>
              {u.phone && <div className="text-xs text-slate-500 font-mono">{u.phone}</div>}
            </div>
            <div className="col-span-3 text-slate-700 truncate">{u.email}</div>
            <div className="col-span-1">
              <Badge variant="outline" className={u.role === "admin" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-slate-50 text-slate-700 border-slate-200"}>{u.role}</Badge>
            </div>
            <div className="col-span-2 font-mono text-slate-700">{u.role === "staff" ? `₹${(u.hourly_rate ?? 0).toFixed(2)}` : "—"}</div>
            <div className="col-span-2 font-mono text-slate-700">{u.role === "staff" ? `₹${(u.km_rate ?? 2).toFixed(2)}` : "—"}</div>
            <div className="col-span-1 text-right flex items-center justify-end gap-1">
              {u.role === "staff" && <EditRatesDialog user={u} onSaved={load} />}
              <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`} className="text-slate-400 hover:text-rose-600"><TrashSimple size={16} /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Reports() {
  const [day, setDay] = useState(new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [daily, setDaily] = useState(null);
  const [monthly, setMonthly] = useState(null);

  const loadDaily = async () => { const { data } = await api.get("/reports/daily", { params: { day } }); setDaily(data); };
  const loadMonthly = async () => { const { data } = await api.get("/reports/monthly-km", { params: { month } }); setMonthly(data); };
  useEffect(() => { loadDaily(); }, [day]);
  useEffect(() => { loadMonthly(); }, [month]);

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Daily task report</div>
        <h1 className="font-display font-black tracking-tighter text-4xl mb-4">Reports</h1>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Label className="overline">Day</Label>
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-48" data-testid="report-day-input" />
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-3">Staff</div>
            <div className="col-span-1">Total</div>
            <div className="col-span-1">Done</div>
            <div className="col-span-1">Open</div>
            <div className="col-span-1">Deferred</div>
            <div className="col-span-2">KM</div>
            <div className="col-span-3">Tasks</div>
          </div>
          {(daily?.summary || []).map((s) => (
            <div key={s.user.id} className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-slate-100" data-testid={`report-row-${s.user.id}`}>
              <div className="col-span-3 font-semibold">{s.user.name}</div>
              <div className="col-span-1 font-mono">{s.total}</div>
              <div className="col-span-1 font-mono text-emerald-600">{s.completed}</div>
              <div className="col-span-1 font-mono text-amber-600">{s.pending + s.in_progress}</div>
              <div className="col-span-1 font-mono text-rose-600">{s.deferred}</div>
              <div className="col-span-2 font-mono font-bold">{s.distance_km.toFixed(2)} km</div>
              <div className="col-span-3 text-xs text-slate-600 space-y-0.5">
                {s.tasks.map((t) => (<div key={t.id}>· {t.title} <span className="text-slate-400">({t.status})</span></div>))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="overline mb-2">Monthly km summary</div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Label className="overline">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="report-month-input" />
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-6">Staff</div>
            <div className="col-span-3">Completed</div>
            <div className="col-span-3">Total KM</div>
          </div>
          {(monthly?.rows || []).map((r) => (
            <div key={r.user.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`monthly-row-${r.user.id}`}>
              <div className="col-span-6 font-semibold">{r.user.name}</div>
              <div className="col-span-3 font-mono">{r.tasks}</div>
              <div className="col-span-3 font-mono font-bold text-indigo-600">{r.distance_km.toFixed(2)} km</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <Layout nav={NAV}>
      <Routes>
        <Route index element={<Overview />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="attendance" element={<AttendanceAdmin />} />
        <Route path="staff" element={<StaffTab />} />
        <Route path="payroll" element={<Payroll />} />
        <Route path="reports" element={<Reports />} />
      </Routes>
    </Layout>
  );
}

function AttendanceAdmin() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState("all");
  const [rows, setRows] = useState([]);
  const [today, setToday] = useState([]);

  const load = async () => {
    const params = { month };
    if (selected !== "all") params.user_id = selected;
    const [{ data: list }, { data: tday }, { data: users }] = await Promise.all([
      api.get("/attendance/list", { params }),
      api.get("/attendance/today"),
      api.get("/users"),
    ]);
    setRows(list);
    setToday(tday);
    setStaff(users.filter((u) => u.role === "staff"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, selected]);

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";

  return (
    <div className="space-y-8">
      <div>
        <div className="overline mb-2">Attendance</div>
        <h1 className="font-display font-black tracking-tighter text-4xl">Today · live board</h1>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
          <div className="col-span-4">Staff</div>
          <div className="col-span-2">Punch in</div>
          <div className="col-span-2">Punch out</div>
          <div className="col-span-2">Hours</div>
          <div className="col-span-2">Status</div>
        </div>
        {staff.map((s) => {
          const row = today.find((t) => t.user_id === s.id);
          const status = !row ? "absent" : row.punch_out_at ? "out" : "in";
          const badge = { absent: "bg-slate-100 text-slate-600 border-slate-200", in: "bg-emerald-50 text-emerald-700 border-emerald-200", out: "bg-indigo-50 text-indigo-700 border-indigo-200" }[status];
          return (
            <div key={s.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`today-attendance-${s.id}`}>
              <div className="col-span-4 font-semibold">{s.name}</div>
              <div className="col-span-2 font-mono">{fmtTime(row?.punch_in_at)}</div>
              <div className="col-span-2 font-mono">{fmtTime(row?.punch_out_at)}</div>
              <div className="col-span-2 font-mono">{row?.hours_worked ? row.hours_worked.toFixed(2) : "—"}</div>
              <div className="col-span-2"><Badge variant="outline" className={`${badge} border capitalize`}>{status === "in" ? "Punched in" : status === "out" ? "Punched out" : "Absent"}</Badge></div>
            </div>
          );
        })}
        {staff.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No staff yet</div>}
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="overline">Log</span>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="attendance-month-input" />
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="w-48" data-testid="attendance-staff-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {staff.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
            <div className="col-span-3">Date</div>
            <div className="col-span-3">Staff</div>
            <div className="col-span-2">In</div>
            <div className="col-span-2">Out</div>
            <div className="col-span-2">Hours</div>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100" data-testid={`att-row-${r.id}`}>
              <div className="col-span-3 font-mono">{r.date}</div>
              <div className="col-span-3">{r.user_name}</div>
              <div className="col-span-2 font-mono">{fmtTime(r.punch_in_at)}</div>
              <div className="col-span-2 font-mono">{fmtTime(r.punch_out_at)}</div>
              <div className="col-span-2 font-mono font-bold">{(r.hours_worked || 0).toFixed(2)}</div>
            </div>
          ))}
          {rows.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No attendance yet for this month.</div>}
        </div>
      </div>
    </div>
  );
}

function Payroll() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reports/payout", { params: { month } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Monthly payout</div>
          <h1 className="font-display font-black tracking-tighter text-4xl">Payroll</h1>
          <p className="text-slate-500 mt-1">Salary (hours × ₹/hr) + Conveyance (KM × ₹/km), computed live.</p>
        </div>
        <div className="flex items-center gap-3">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="payroll-month-input" />
          <div className="stat-card rounded-2xl px-5 py-3 min-w-[200px]" data-testid="payroll-grand-total">
            <div className="overline">Grand total</div>
            <div className="font-display font-black tracking-tighter text-2xl text-indigo-600">₹{(data?.grand_total ?? 0).toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200 overline">
          <div className="col-span-3">Staff</div>
          <div className="col-span-1">Days</div>
          <div className="col-span-1">Hours</div>
          <div className="col-span-2">₹/hr · ₹/km</div>
          <div className="col-span-1">KM</div>
          <div className="col-span-1">Salary</div>
          <div className="col-span-1">Conv.</div>
          <div className="col-span-2 text-right">Total payable</div>
        </div>
        {(data?.rows || []).map((r) => (
          <div key={r.user.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-slate-100 items-center" data-testid={`payroll-row-${r.user.id}`}>
            <div className="col-span-3 font-semibold">{r.user.name}</div>
            <div className="col-span-1 font-mono">{r.present_days}</div>
            <div className="col-span-1 font-mono">{r.hours.toFixed(2)}</div>
            <div className="col-span-2 font-mono text-xs">₹{r.hourly_rate.toFixed(2)} · ₹{r.km_rate.toFixed(2)}</div>
            <div className="col-span-1 font-mono">{r.distance_km.toFixed(2)}</div>
            <div className="col-span-1 font-mono">₹{r.salary.toFixed(2)}</div>
            <div className="col-span-1 font-mono">₹{r.conveyance.toFixed(2)}</div>
            <div className="col-span-2 text-right font-mono font-black text-lg text-indigo-600">₹{r.total.toFixed(2)}</div>
          </div>
        ))}
        {(data?.rows || []).length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No staff to compute payroll for.</div>}
      </div>
    </div>
  );
}
